import React, { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Box, Paper, Typography, MenuItem, Select, FormControl, InputLabel, Button,
  Table, TableBody, TableCell, TableHead, TableRow, Chip, CircularProgress, Tooltip,
} from '@mui/material'
import RefreshIcon from '@mui/icons-material/Refresh'
import InsightsIcon from '@mui/icons-material/Insights'
import HiveIcon from '@mui/icons-material/Hive'
import LocationOnIcon from '@mui/icons-material/LocationOn'
import FavoriteIcon from '@mui/icons-material/Favorite'
import WaterDropIcon from '@mui/icons-material/WaterDrop'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import RouterIcon from '@mui/icons-material/Router'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import {
  ResponsiveContainer, AreaChart, Area, BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip as RTooltip, Legend, LabelList,
} from 'recharts'
import { getAnalyticsOverview, AnalyticsOverview, AnalyticsInsight } from '../services/analytics'

const HEALTH_COLORS = ['#2e7d32', '#66bb6a', '#ffb300', '#ef5350']
const PIE_COLORS = ['#f6b400', '#d18a00', '#8d6e63', '#26a69a', '#5c6bc0', '#ec407a', '#66bb6a']

/** "2026-03" → short month + 2-digit year in the UI language ("Mar 26", "מרץ 26"). */
const fmtMonth = (m: string, locale: string): string => {
  const [y, mo] = m.split('-')
  return new Date(parseInt(y, 10), parseInt(mo, 10) - 1, 1).toLocaleDateString(locale, { month: 'short', year: '2-digit' })
}

// Compact for large values (52K, 1.2M); full thousands-separated below 10k.
const compactNum = (n: number, locale: string): string =>
  n >= 10000 ? new Intl.NumberFormat(locale, { notation: 'compact', maximumFractionDigits: 1 }).format(n) : n.toLocaleString(locale)
const fullNum = (n: number, locale: string): string => n.toLocaleString(locale)
// For "live / total" pairs: keep both sides in the same format so they compare at a glance.
const ratioNum = (n: number, locale: string): string =>
  n >= 100000 ? new Intl.NumberFormat(locale, { notation: 'compact', maximumFractionDigits: 1 }).format(n) : n.toLocaleString(locale)

/** Camel-case treatment codes (from voice recordings / recommendations) → translation keys. */
const TREATMENT_CODE_KEYS: Record<string, string> = {
  others: 'analytics.treatmentTypes.other',
  amitraz: 'analytics.treatmentTypes.amitraz',
  varroa: 'tasks.types.varroaTreatment',
  sugarFeeding: 'tasks.types.sugarSyrup',
  pollenPatty: 'tasks.types.pollenSubstitute',
  feeding: 'tasks.types.feeding',
  treatment: 'tasks.types.treatment',
}

/** Treatment types arrive as free English text; map the known ones to translation keys. */
const TREATMENT_KEYS: Record<string, string> = {
  'apivar': 'analytics.treatmentTypes.apivar',
  'formic pro': 'analytics.treatmentTypes.formicPro',
  'oxalic acid': 'analytics.treatmentTypes.oxalicAcid',
  'amitraz (vapor)': 'analytics.treatmentTypes.amitrazVapor',
  'apiguard': 'analytics.treatmentTypes.apiguard',
  'amitraz': 'analytics.treatmentTypes.amitraz',
  'other': 'analytics.treatmentTypes.other',
  'others': 'analytics.treatmentTypes.other',
  'treatment': 'analytics.treatmentTypes.general',
  'feeding': 'analytics.treatmentTypes.feeding',
  'pollenpatty': 'analytics.treatmentTypes.pollenPatty',
  'sugarfeeding': 'yardDetails.taskTypes.feedSugarSyrup',
  'varroa': 'yardDetails.taskTypes.treatVarroaMites',
  'treat for varroa mites': 'yardDetails.taskTypes.treatVarroaMites',
  'feed sugar syrup': 'yardDetails.taskTypes.feedSugarSyrup',
  'feed pollen substitute': 'yardDetails.taskTypes.feedPollenSubstitute',
  'requeen hive': 'yardDetails.taskTypes.reqeenHive',
  'replace old frames': 'yardDetails.taskTypes.replaceOldFrames',
  'clean hive equipment': 'yardDetails.taskTypes.cleanHiveEquipment',
}

const AnalysisScreen: React.FC = () => {
  const { t, i18n } = useTranslation()
  const locale = i18n.language || 'en'
  const [data, setData] = useState<AnalyticsOverview | null>(null)
  const [loading, setLoading] = useState(true)
  const [months, setMonths] = useState(12)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const res = await getAnalyticsOverview(months)
      setData(res.data)
    } catch (e) {
      console.error('Failed to load analytics', e)
    } finally {
      setLoading(false)
    }
  }, [months])

  useEffect(() => { load() }, [load])

  if (loading && !data) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}><CircularProgress /></Box>
  }
  if (!data) {
    return <Box sx={{ p: 3 }}><Typography color="text.secondary">{t('analytics.noData')}</Typography></Box>
  }

  const k = data.kpis
  const healthLabels = [t('analytics.healthVeryGood'), t('analytics.healthGood'), t('analytics.healthNormal'), t('analytics.healthBad')]
  const healthDist = data.healthDistribution.map((d, i) => ({ name: healthLabels[i] || d.name, value: d.value }))
  const treatmentLabel = (name: string) => {
    const key = TREATMENT_KEYS[name.trim().toLowerCase()] || TREATMENT_CODE_KEYS[name.trim()]
    return key ? t(key) : name
  }
  // Different stored spellings can share a label ("varroa" / "Treat for Varroa Mites") — merge them
  const treatmentTotals = new Map<string, number>()
  for (const d of data.treatmentsByType) {
    const label = treatmentLabel(d.name)
    treatmentTotals.set(label, (treatmentTotals.get(label) || 0) + d.value)
  }
  const treatments = Array.from(treatmentTotals, ([name, value]) => ({ name, value }))
    .sort((a, b) => b.value - a.value)
  const isRtl = i18n.dir() === 'rtl'
  const honeyTypes = data.harvestByType
    .map(d => ({ name: t(`inventory.honeyTypes.${d.name}`, { defaultValue: d.name }), value: d.value }))
    .sort((a, b) => b.value - a.value)
  const honeyTotal = honeyTypes.reduce((sum, d) => sum + d.value, 0)

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="h5" sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 700 }}>
          <InsightsIcon /> {t('analytics.title')}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>{t('analytics.period')}</InputLabel>
            <Select label={t('analytics.period')} value={months} onChange={e => setMonths(Number(e.target.value))}>
              <MenuItem value={3}>{t('analytics.last3')}</MenuItem>
              <MenuItem value={6}>{t('analytics.last6')}</MenuItem>
              <MenuItem value={12}>{t('analytics.last12')}</MenuItem>
              <MenuItem value={24}>{t('analytics.last24')}</MenuItem>
            </Select>
          </FormControl>
          <Button startIcon={<RefreshIcon />} onClick={load}>{t('analytics.refresh')}</Button>
        </Box>
      </Box>

      {/* KPI cards */}
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 2, mb: 3 }}>
        <Kpi icon={<LocationOnIcon />} label={t('analytics.kpiSites')} value={compactNum(k.sites, locale)} tooltip={fullNum(k.sites, locale)} color="#1976d2" />
        <Kpi icon={<HiveIcon />} label={t('analytics.kpiHives')} value={`${ratioNum(k.liveHives, locale)} / ${ratioNum(k.totalHives, locale)}`} tooltip={`${fullNum(k.liveHives, locale)} / ${fullNum(k.totalHives, locale)}`} color="#8d6e63" />
        <Kpi icon={<FavoriteIcon />} label={t('analytics.kpiHealth')} value={`${k.healthRate}%`} color={k.healthRate >= 70 ? '#2e7d32' : k.healthRate >= 50 ? '#f6b400' : '#ef5350'} />
        <Kpi icon={<WaterDropIcon />} label={t('analytics.kpiHoney')} value={compactNum(k.totalHoney, locale)} tooltip={fullNum(k.totalHoney, locale)} color="#f6b400" />
        <Kpi icon={<WarningAmberIcon />} label={t('analytics.kpiAlerts')} value={compactNum(k.openAlerts, locale)} tooltip={fullNum(k.openAlerts, locale)} color={k.openAlerts ? '#ef5350' : '#9e9e9e'} />
        <Kpi icon={<RouterIcon />} label={t('analytics.kpiGateways')} value={`${ratioNum(k.onlineGateways, locale)} / ${ratioNum(k.gateways, locale)}`} tooltip={`${fullNum(k.onlineGateways, locale)} / ${fullNum(k.gateways, locale)}`} color="#26a69a" />
        <Kpi icon={<RouterIcon />} label={t('analytics.kpiDevices')} value={compactNum(k.activeDevices, locale)} tooltip={fullNum(k.activeDevices, locale)} color="#5c6bc0" />
        {k.queenRightRate != null && <Kpi icon={<FavoriteIcon />} label={t('analytics.kpiQueen')} value={`${k.queenRightRate}%`} color="#ab47bc" />}
      </Box>

      {/* Insights */}
      <Paper sx={{ p: 2, mb: 3, borderRadius: 2 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
          <InsightsIcon fontSize="small" /> {t('analytics.keyInsights')}
        </Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 1.5 }}>
          {data.insights.map((ins, i) => <InsightCard key={i} insight={ins} />)}
        </Box>
      </Paper>

      {/* Charts grid */}
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(360px, 1fr))', gap: 2 }}>
        <ChartCard title={t('analytics.chartHealthTrend')}>
          <ResponsiveContainer width="100%" height={240}>
            <AreaChart data={data.healthTrend.map(d => ({ ...d, label: fmtMonth(d.month, locale) }))}>
              <defs>
                <linearGradient id="hr" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#2e7d32" stopOpacity={0.5} />
                  <stop offset="95%" stopColor="#2e7d32" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="label" tick={{ fontSize: 11 }} />
              <YAxis domain={[0, 100]} tick={{ fontSize: 11 }} />
              <RTooltip />
              <Area type="monotone" dataKey="healthRate" stroke="#2e7d32" strokeWidth={2} fill="url(#hr)" connectNulls name={t('analytics.kpiHealth')} />
            </AreaChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title={t('analytics.chartHealthDist')}>
          <ResponsiveContainer width="100%" height={240}>
            <PieChart>
              <Pie data={healthDist} dataKey="value" nameKey="name" innerRadius={55} outerRadius={85} paddingAngle={2}>
                {healthDist.map((_, i) => <Cell key={i} fill={HEALTH_COLORS[i % HEALTH_COLORS.length]} />)}
              </Pie>
              <RTooltip />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title={t('analytics.chartHarvest')}>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={data.harvestTrend.map(d => ({ ...d, label: fmtMonth(d.month, locale) }))}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="label" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} />
              <RTooltip />
              <Bar dataKey="quantity" fill="#f6b400" radius={[4, 4, 0, 0]} name={t('analytics.kpiHoney')} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title={t('analytics.chartVarroa')}>
          <ResponsiveContainer width="100%" height={240}>
            <LineChart data={data.varroaTrend.map(d => ({ ...d, label: fmtMonth(d.month, locale) }))}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="label" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} />
              <RTooltip />
              <Line type="monotone" dataKey="varroa" stroke="#ef5350" strokeWidth={2} connectNulls dot={{ r: 3 }} name={t('analytics.varroaPer100')} />
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>

        {honeyTypes.length > 0 && (
          <ChartCard title={t('analytics.chartHoneyType')}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
              <Box sx={{ width: 200, height: 200, flexShrink: 0, mx: 'auto' }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={honeyTypes} dataKey="value" nameKey="name" innerRadius={50} outerRadius={90} paddingAngle={2} isAnimationActive={false}>
                      {honeyTypes.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                    </Pie>
                    <RTooltip formatter={(v) => fullNum(Number(v), locale)} />
                  </PieChart>
                </ResponsiveContainer>
              </Box>
              <Box sx={{ flex: 1, minWidth: 160, display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                {honeyTypes.map((d, i) => (
                  <Box key={d.name} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: PIE_COLORS[i % PIE_COLORS.length], flexShrink: 0 }} />
                    <Typography variant="body2" sx={{ flex: 1 }}>{d.name}</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>{compactNum(d.value, locale)}</Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ minWidth: 36, textAlign: 'end' }}>
                      {honeyTotal ? Math.round((d.value / honeyTotal) * 100) : 0}%
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          </ChartCard>
        )}

        {treatments.length > 0 && (
          <ChartCard title={t('analytics.chartTreatments')}>
            {/* SVG text anchoring breaks under an RTL parent; lay out LTR and mirror the axes for RTL */}
            <Box dir="ltr">
              <ResponsiveContainer width="100%" height={Math.max(200, treatments.length * 34)}>
                <BarChart layout="vertical" data={treatments} margin={{ top: 4, right: isRtl ? 8 : 36, bottom: 4, left: isRtl ? 36 : 8 }}>
                  <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                  <XAxis type="number" allowDecimals={false} reversed={isRtl} tick={{ fontSize: 11 }} />
                  <YAxis type="category" dataKey="name" orientation={isRtl ? 'right' : 'left'} width={180} tick={{ fontSize: 12 }} interval={0} />
                  <RTooltip cursor={{ fill: 'rgba(92,107,192,0.08)' }} />
                  <Bar dataKey="value" fill="#5c6bc0" radius={isRtl ? [4, 0, 0, 4] : [0, 4, 4, 0]} barSize={18} name={t('analytics.treatmentCount')}>
                    <LabelList
                      dataKey="value"
                      content={(p: any) => {
                        // Place the count just past the bar's free end (the axis is mirrored in RTL)
                        const x0 = Number(p.x), w = Number(p.width)
                        const edge = isRtl ? Math.min(x0, x0 + w) - 6 : Math.max(x0, x0 + w) + 6
                        return <text x={edge} y={Number(p.y) + Number(p.height) / 2} dy={4} textAnchor={isRtl ? 'end' : 'start'} fontSize={12} fontWeight={700} fill="#37474f">{p.value}</text>
                      }}
                    />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </Box>
          </ChartCard>
        )}
      </Box>

      {/* Yard ranking */}
      <Paper sx={{ mt: 3, p: 2, borderRadius: 2 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>{t('analytics.sitesByHealth')}</Typography>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{t('analytics.colSite')}</TableCell>
              <TableCell align="center">{t('analytics.colHives')}</TableCell>
              <TableCell align="center">{t('analytics.colHealth')}</TableCell>
              <TableCell align="center">{t('analytics.colDead')}</TableCell>
              <TableCell align="center">{t('analytics.colOpenAlerts')}</TableCell>
              <TableCell>{t('analytics.colLastVisit')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.yardRanking.map(y => (
              <TableRow key={y.id} hover>
                <TableCell sx={{ fontWeight: 600 }}>{y.name}</TableCell>
                <TableCell align="center">{y.hives}</TableCell>
                <TableCell align="center">
                  <Chip size="small" label={`${y.healthRate}%`} color={y.healthRate >= 70 ? 'success' : y.healthRate >= 50 ? 'warning' : 'error'} />
                </TableCell>
                <TableCell align="center">{y.dead || '—'}</TableCell>
                <TableCell align="center">{y.openAlerts || '—'}</TableCell>
                <TableCell>{y.lastVisit ? new Date(y.lastVisit).toLocaleDateString(locale) : '—'}</TableCell>
              </TableRow>
            ))}
            {data.yardRanking.length === 0 && (
              <TableRow><TableCell colSpan={6} align="center" sx={{ py: 3, color: 'text.secondary' }}>{t('analytics.noSiteData')}</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>

      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
        {t('analytics.generatedAt', { date: new Date(data.generatedAt).toLocaleString(locale) })}
      </Typography>
    </Box>
  )
}

const Kpi: React.FC<{ icon: React.ReactNode; label: string; value: React.ReactNode; color: string; tooltip?: string }> = ({ icon, label, value, color, tooltip }) => (
  <Paper sx={{ p: 2, borderRadius: 2, borderTop: `3px solid ${color}`, minWidth: 0 }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color, mb: 0.5 }}>{icon}
      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{label}</Typography>
    </Box>
    <Tooltip title={tooltip || ''} disableHoverListener={!tooltip}>
      <Typography sx={{ fontWeight: 700, fontSize: 'clamp(1.3rem, 1.8vw, 1.8rem)', whiteSpace: 'nowrap', lineHeight: 1.3 }}><bdi dir="ltr">{value}</bdi></Typography>
    </Tooltip>
  </Paper>
)

const severityStyle: Record<AnalyticsInsight['severity'], { color: string; bg: string; icon: React.ReactNode }> = {
  critical: { color: '#c62828', bg: 'rgba(239,83,80,0.10)', icon: <ErrorOutlineIcon fontSize="small" /> },
  warning: { color: '#ef6c00', bg: 'rgba(255,179,0,0.12)', icon: <WarningAmberIcon fontSize="small" /> },
  positive: { color: '#2e7d32', bg: 'rgba(102,187,106,0.12)', icon: <CheckCircleOutlineIcon fontSize="small" /> },
  info: { color: '#1565c0', bg: 'rgba(25,118,210,0.10)', icon: <InfoOutlinedIcon fontSize="small" /> },
}

const InsightCard: React.FC<{ insight: AnalyticsInsight }> = ({ insight }) => {
  const { t } = useTranslation()
  const s = severityStyle[insight.severity]
  const base = insight.code ? `analytics.insights.${insight.code}` : ''
  const title = base ? t(`${base}.title`, { ...insight.params, defaultValue: insight.title }) : insight.title
  const detail = base ? t(`${base}.detail`, { ...insight.params, defaultValue: insight.detail }) : insight.detail
  return (
    <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: s.bg, borderLeft: `4px solid ${s.color}`, display: 'flex', gap: 1 }}>
      <Box sx={{ color: s.color, mt: '2px' }}>{s.icon}</Box>
      <Box>
        <Typography variant="body2" sx={{ fontWeight: 700, color: s.color }}>{title}</Typography>
        <Typography variant="caption" color="text.secondary">{detail}</Typography>
      </Box>
    </Box>
  )
}

const ChartCard: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <Paper sx={{ p: 2, borderRadius: 2 }}>
    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>{title}</Typography>
    {children}
  </Paper>
)

export default AnalysisScreen
